import LoadingTableItems from "@/components/LoadingTableItems.tsx";
import TableSortHeader from "@/components/TableSortHeader.tsx";
import ConfirmDeleteButton from "@/components/ConfirmDeleteButton.tsx";
import {notify} from "@/components/Notifications.tsx";
import EmptyTableRow from "@/components/EmptyTableRow.tsx";
import TablePagination from "@/components/TablePagination.tsx";
import {SortField, SortOrder, TagPage} from "@/hooks/useTags.ts";
import {Pencil} from "lucide-react";

type Props = {
    content: TagPage;
    isLoading?: boolean;
    isError?: boolean;
    onCreate: () => void;
    onEdit?: (id: string) => void;
    onDelete?: (id: string) => Promise<unknown>;
    setPage: (p: number) => void;
    sortBy: SortField;
    sortOrder: SortOrder;
    onRequestSort: (field: SortField) => void;
};

export default function TagTable({
                                     content,
                                     isLoading,
                                     isError,
                                     setPage,
                                     onCreate,
                                     onEdit,
                                     onDelete,
                                     sortBy,
                                     sortOrder,
                                     onRequestSort,
                                 }: Props) {

    if (isLoading) return <LoadingTableItems/>
    if (isError) return <p className="mt-6 text-sm text-red-600">Failed to load tags.</p>;

    return (
        <div className="mt-8 flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                    <div className="overflow-hidden shadow-sm outline-1 outline-black/5 sm:rounded-lg">
                        <table className="relative min-w-full divide-y divide-gray-300">
                            <thead className="bg-gray-50">
                            <tr>
                                <TableSortHeader<SortField>
                                    field="name"
                                    activeField={sortBy}
                                    order={sortOrder}
                                    onSort={onRequestSort}
                                    className="py-3.5 pr-3 pl-4 sm:pl-6"
                                    thProps={{scope: "col"}}
                                >
                                    Name
                                </TableSortHeader>

                                <th className="px-3 py-3.5 text-left text-sm font-semibold text-blue-900">Description</th>

                                <th className="px-3 py-3.5 text-right text-sm font-semibold text-blue-900">
                                    <span className="sr-only">Actions</span>
                                </th>
                            </tr>
                            </thead>

                            <tbody className="divide-y divide-gray-200 bg-white">
                            {content.items.map((i) => (
                                <tr key={i.id}>
                                    <td className="py-4 pr-3 pl-4 text-sm font-medium text-blue-900 sm:pl-6 whitespace-normal">
                                        {i.name}
                                    </td>
                                    <td className="px-3 py-4 text-sm text-gray-500 whitespace-normal">{i.description}</td>

                                    <td className="py-4 pr-4 pl-3 text-right text-sm font-medium whitespace-nowrap sm:pr-6">
                                        <div className="inline-flex items-center gap-4">
                                            {/* Edit */}
                                            {onEdit && (
                                                <button
                                                    type="button"
                                                    onClick={() => onEdit(i.id)}
                                                    className="text-yellow-600 hover:text-yellow-500"
                                                    aria-label={`Edit ${i.name}`}
                                                >
                                                    <Pencil/>
                                                </button>
                                            )}

                                            {/* Delete */}
                                            {onDelete && (
                                                <ConfirmDeleteButton
                                                    onDelete={() => onDelete(i.id)}
                                                    itemLabel="Tag"
                                                    itemName={i.name}
                                                    onSuccess={() => notify.success("Tag deleted.")}
                                                    className="text-red-600 hover:text-red-500"
                                                />
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}

                            {content.items.length === 0 && (
                                <EmptyTableRow
                                    colSpan={7}
                                    title="No tags found"
                                    description='Click “Add Tag” to create your first.'
                                    action={{label: "Add Tag", onClick: onCreate}}
                                    size="lg"
                                />
                            )}
                            </tbody>
                        </table>

                        <TablePagination
                            page={content.page}
                            size={content.size}
                            total={content.total}
                            onPage={setPage}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
